import { useId, useState } from "react";
import { useCompactShell } from "../layouts/useCompactShell";
import { Button } from "../components/Button";
import { SearchClearButton } from "../components/SearchClearButton";
import { useCollection } from "./CollectionContext";
import { useCollectionView } from "./CollectionViewContext";
import type { CollectionRarityFilter } from "./collection-filters";
import {
  COLLECTION_DEFAULT_SORT,
  COLLECTION_SORT_OPTIONS,
  type CollectionUiSort,
} from "./collection-sort";

const RARITY_OPTIONS: Array<{ value: CollectionRarityFilter; label: string }> = [
  { value: "", label: "Все редкости" },
  { value: "TH", label: "TH" },
  { value: "STH", label: "STH" },
  { value: "Exclusive", label: "Exclusive" },
];

function filtersNeedReset(view: ReturnType<typeof useCollectionView>): boolean {
  return (
    view.filters.query.trim() !== "" ||
    view.filters.year !== "" ||
    view.filters.series !== "" ||
    view.filters.rarity !== "" ||
    view.filters.sort !== COLLECTION_DEFAULT_SORT
  );
}

function activeFacetCount(view: ReturnType<typeof useCollectionView>): number {
  let count = 0;
  if (view.filters.year !== "") {
    count += 1;
  }
  if (view.filters.series !== "") {
    count += 1;
  }
  if (view.filters.rarity !== "") {
    count += 1;
  }
  return count;
}

function CollectionFacetFields({
  formId,
  className = "hw-catalog-toolbar__filters hw-collection-filters__facets",
}: {
  formId: string;
  className?: string;
}) {
  const view = useCollectionView();

  return (
    <div className={className}>
      <label className="hw-select-field hw-select-field--year" htmlFor={`${formId}-year`}>
        <span className="hw-visually-hidden">Год коллекции</span>
        <select
          id={`${formId}-year`}
          className="hw-select"
          aria-label="Год коллекции"
          value={view.filters.year}
          onChange={(event) => view.setYear(event.target.value)}
        >
          <option value="">Все годы</option>
          {view.yearOptions.map((option) => (
            <option key={option.value} value={option.value} title={option.description}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <label className="hw-select-field hw-select-field--series" htmlFor={`${formId}-series`}>
        <span className="hw-visually-hidden">Серия коллекции</span>
        <select
          id={`${formId}-series`}
          className="hw-select"
          aria-label="Серия коллекции"
          value={view.filters.series}
          disabled={!view.seriesEnabled}
          onChange={(event) => view.setSeries(event.target.value)}
        >
          <option value="">
            {view.seriesEnabled ? "Все серии" : "Сначала год"}
          </option>
          {view.seriesOptions.map((option) => (
            <option key={option.value} value={option.value} title={option.description}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <label className="hw-select-field hw-select-field--rarity" htmlFor={`${formId}-rarity`}>
        <span className="hw-visually-hidden">Редкость коллекции</span>
        <select
          id={`${formId}-rarity`}
          className="hw-select"
          aria-label="Редкость коллекции"
          value={view.filters.rarity}
          onChange={(event) =>
            view.setRarity(event.target.value as CollectionRarityFilter)
          }
        >
          {RARITY_OPTIONS.map((option) => (
            <option key={option.value || "all"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function CollectionSortField({ formId }: { formId: string }) {
  const view = useCollectionView();

  return (
    <label className="hw-select-field hw-select-field--sort" htmlFor={`${formId}-sort`}>
      <span className="hw-visually-hidden">Сортировка коллекции</span>
      <select
        id={`${formId}-sort`}
        className="hw-select"
        aria-label="Сортировка коллекции"
        title={
          COLLECTION_SORT_OPTIONS.find((option) => option.value === view.filters.sort)
            ?.description
        }
        value={view.filters.sort}
        onChange={(event) => view.setSort(event.target.value as CollectionUiSort)}
      >
        {COLLECTION_SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value} title={option.description}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function clearFacetFilters(view: ReturnType<typeof useCollectionView>): void {
  view.setYear("");
  view.setSeries("");
  view.setRarity("");
}

export function CollectionSearchField({ className = "" }: { className?: string }) {
  const view = useCollectionView();
  const formId = useId();

  return (
    <div className={`hw-search-field ${className}`.trim()}>
      <label className="hw-visually-hidden" htmlFor={`${formId}-q`}>
        Поиск в коллекции
      </label>
      <input
        id={`${formId}-q`}
        className="hw-field__input hw-catalog-toolbar__input"
        value={view.filters.query}
        onChange={(event) => view.setQuery(event.target.value)}
        placeholder="Название, № или набор"
        autoComplete="off"
        enterKeyHint="search"
      />
      <SearchClearButton
        value={view.filters.query}
        onClear={() => view.setQuery("")}
      />
    </div>
  );
}

export function CollectionFilters() {
  const collection = useCollection();
  const view = useCollectionView();
  const formId = useId();
  const compactShell = useCompactShell();
  const [filtersOpen, setFiltersOpen] = useState(false);

  if (!compactShell || collection.status !== "ready" || collection.items.length === 0) {
    return null;
  }

  const activeCount = activeFacetCount(view);
  const filtersLabel =
    activeCount > 0 ? `Фильтры · ${activeCount}` : "Фильтры";
  const canClearFacets = activeCount > 0;

  return (
    <div className="hw-collection-filters hw-collection-filters--mobile">
      <div className="hw-collection-filters__bar">
        <Button
          type="button"
          variant="ghost"
          className="hw-collection-filters__toggle"
          aria-expanded={filtersOpen}
          aria-controls={`${formId}-panel`}
          onClick={() => setFiltersOpen((value) => !value)}
        >
          {filtersLabel}
        </Button>
        <CollectionSortField formId={formId} />
      </div>

      {filtersOpen ? (
        <form
          id={`${formId}-panel`}
          className="hw-catalog-toolbar hw-collection-filters__panel"
          onSubmit={(event) => event.preventDefault()}
          aria-label="Фильтры коллекции"
        >
          <CollectionFacetFields formId={formId} />
          {canClearFacets ? (
            <div className="hw-collection-filters__reset">
              <Button
                type="button"
                variant="ghost"
                className="hw-catalog-toolbar__reset"
                onClick={() => clearFacetFilters(view)}
              >
                Сбросить
              </Button>
            </div>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}

export function CollectionToolbar() {
  const collection = useCollection();
  const view = useCollectionView();
  const formId = useId();
  const compactShell = useCompactShell();

  if (compactShell || collection.status !== "ready" || collection.items.length === 0) {
    return null;
  }

  const canReset = filtersNeedReset(view);

  return (
    <form
      className="hw-catalog-toolbar hw-collection-filters hw-collection-filters--desktop"
      onSubmit={(event) => event.preventDefault()}
      role="search"
      aria-label="Фильтры коллекции"
    >
      <div className="hw-collection-filters__search hw-collection-filters__search--desktop">
        <CollectionSearchField />
        {canReset ? (
          <Button
            type="button"
            variant="ghost"
            className="hw-catalog-toolbar__reset"
            onClick={() => view.resetFilters()}
          >
            Сброс
          </Button>
        ) : null}
      </div>
      <div className="hw-catalog-toolbar__filters">
        <CollectionFacetFields formId={formId} className="hw-collection-filters__facets--inline" />
        <CollectionSortField formId={formId} />
      </div>
    </form>
  );
}
